/**
 * dateUtils
 *
 * Locale-aware date / datetime formatters used across the UI.
 *
 * Why this exists:
 *   Several pages (OutreachLandingPage, AdminMarketingTargetsPage,
 *   AdminOutreachCampaignsPage, AdminConsultationsPage, AdminUsersPage,
 *   AdminUserPropertiesPage, AdminConversationFeedbackPage, etc.) defined
 *   their own `formatDate` / `formatDateTime` with slightly different
 *   handling of null/invalid values and timezone edge-cases. This module
 *   centralizes that behavior.
 *
 * Note:
 *   For user-timezone-aware rendering (calendar, email, scheduled events)
 *   use `timezoneUtils.js` — those helpers integrate with the user's saved
 *   timezone preference. The helpers here are plain locale formatters.
 */

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const str = String(value);

  // Parse YYYY-MM-DD as a LOCAL date (not UTC midnight) to avoid the
  // common bug where "2026-06-08" renders as Jun 7 in timezones west of
  // UTC because new Date(string) treats date-only ISO inputs as UTC.
  const dateOnly = DATE_ONLY_REGEX.exec(str);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3])
    );
  }

  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const DEFAULT_LOCALE = 'en-CA';

/**
 * Format a date-only value (e.g. "2026-06-08") for display.
 *
 * @param {string | Date | null | undefined} value
 * @param {object} [options]
 * @param {string} [options.locale='en-CA']
 * @param {Intl.DateTimeFormatOptions | null} [options.format] -
 *   Pass `null` to use the runtime default (no format args). Default is
 *   short month + day + year.
 * @param {string | null} [options.fallback=null] - Returned when input
 *   is null / empty / unparseable. Pass `'N/A'` to match older helpers.
 * @returns {string | null}
 */
export const formatDate = (value, options = {}) => {
  const {
    locale = DEFAULT_LOCALE,
    format = { year: 'numeric', month: 'short', day: 'numeric' },
    fallback = null,
  } = options;

  const date = toDate(value);
  if (!date) return fallback;

  try {
    return format
      ? date.toLocaleDateString(locale, format)
      : date.toLocaleDateString(locale);
  } catch (_) {
    return String(value);
  }
};

/**
 * Format a datetime value with both date + time parts.
 *
 * @param {string | Date | null | undefined} value
 * @param {object} [options]
 * @param {string} [options.locale='en-CA']
 * @param {Intl.DateTimeFormatOptions | null} [options.format] - Omit to
 *   use `toLocaleString(locale)` (matches most legacy helpers).
 * @param {string | null} [options.fallback=null]
 * @returns {string | null}
 */
export const formatDateTime = (value, options = {}) => {
  const {
    locale = DEFAULT_LOCALE,
    format,
    fallback = null,
  } = options;

  const date = toDate(value);
  if (!date) return fallback;

  try {
    return format
      ? date.toLocaleString(locale, format)
      : date.toLocaleString(locale);
  } catch (_) {
    return String(value);
  }
};

/**
 * Format the time portion only.
 *
 * @param {string | Date | null | undefined} value
 * @param {object} [options]
 * @param {string} [options.locale='en-CA']
 * @param {Intl.DateTimeFormatOptions} [options.format]
 * @param {string | null} [options.fallback=null]
 * @returns {string | null}
 */
export const formatTime = (value, options = {}) => {
  const {
    locale = DEFAULT_LOCALE,
    format = { hour: 'numeric', minute: '2-digit' },
    fallback = null,
  } = options;

  const date = toDate(value);
  if (!date) return fallback;

  try {
    return date.toLocaleTimeString(locale, format);
  } catch (_) {
    return String(value);
  }
};
